//* Libraries imports
import { motion, AnimatePresence } from "framer-motion";
import { Modal, Button, Spinner, Alert } from "react-bootstrap";
import { FaEye, FaGraduationCap, FaClipboardList } from "react-icons/fa";

//* Hooks imports
import { useGetResultDetails } from "../../../../hooks/queries/result/use-get-details-result";

//* Types imports
import { ExtendedCorrecaoSchema } from "../../../../schemas/common/result/result-extended";

//* Components imports
import Icon from "../../../../components/common/Icon";

//* Styles imports
import "./criteria-details-dialog.scss";

interface CriteriaDetailsDialogProps {
  show: boolean;
  onHide: () => void;
  examId: number | undefined;
  questaoId: number | undefined;
  isEssayQuestion?: boolean;
}

export function CriteriaDetailsDialog({
  show,
  onHide,
  examId,
  questaoId,
  isEssayQuestion = false,
}: CriteriaDetailsDialogProps) {
  const { data: details, isLoading, error } = useGetResultDetails(
    examId, 
    questaoId, 
    isEssayQuestion
  );


  const modalVariants = {
    hidden: { opacity: 0, scale: 0.9, y: -20 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: "spring" as const,
        stiffness: 300,
        damping: 30,
        duration: 0.3,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.9,
      y: -20,
      transition: {
        duration: 0.2,
      },
    },
  } as const;

  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const criteriaVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.3 },
    },
  };
  const getCriteriaName = (criteriaKey: string): string => {
    const criteriaNames: Record<string, string> = {
      c1: "Critério 1",
      c2: "Critério 2",
      c3: "Critério 3",
      c4: "Critério 4",
      c5: "Critério 5",
    };
    return criteriaNames[criteriaKey] || `Critério ${criteriaKey.toUpperCase()}`;
  };

  const getNotaColor = (nota: number): string => {
    if (nota >= 8) return "text-success";
    if (nota >= 6) return "text-warning";
    if (nota >= 4) return "text-info";
    return "text-danger";
  };

  const renderCriteria = (correcao: ExtendedCorrecaoSchema) => {
    const criteria = ['c1', 'c2', 'c3', 'c4', 'c5'] as const;
    
    return criteria.map((criteriaKey) => {
      const criterio = correcao[criteriaKey];
      if (!criterio) return null;

      return (
        <motion.div
          key={criteriaKey}
          variants={criteriaVariants}
          className="criteria-item"
        >
          <div className="criteria-header">
            <h6 className="criteria-title">
              <Icon icon={FaClipboardList} className="me-2" />
              {getCriteriaName(criteriaKey)}
            </h6>
            <div className={`criteria-score ${getNotaColor(criterio.nota)}`}>
              <strong>{criterio.nota.toFixed(1)}/10</strong>
            </div>
          </div>
          <div className="criteria-content">
            <div className="criteria-justification">
              <strong>Justificativa:</strong>
              <p className="mt-2">{criterio.justificativa}</p>
            </div>
          </div>
        </motion.div>
      );
    }).filter(Boolean);
  };

  return (
    <AnimatePresence>
      {show && (
        <Modal
          show={show}
          onHide={onHide}
          size="lg"
          centered
          backdrop={true}
          keyboard={true}
          className="criteria-details-modal"
        >
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {" "}
            <Modal.Header className="border-0 pb-2">
              <Modal.Title className="d-flex align-items-center">
                <Icon icon={FaGraduationCap} className="me-2 text-primary" />
                Detalhes dos Critérios de Correção
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
              <motion.div
                variants={contentVariants}
                initial="hidden"
                animate="visible"
              >
                {/* Loading State */}
                {isLoading && (
                  <motion.div
                    variants={criteriaVariants}
                    className="text-center py-4"
                  >
                    <Spinner animation="border" variant="primary" />
                    <p className="text-muted mt-2">Carregando detalhes...</p>
                  </motion.div>
                )}{" "}
                {/* Error State */}
                {error && (
                  <motion.div variants={criteriaVariants}>
                    <Alert
                      variant="danger"
                      className="d-flex align-items-center"
                    >
                      <Icon icon={FaEye} className="me-2" />
                      Erro ao carregar os detalhes dos critérios. Tente
                      novamente.
                    </Alert>
                  </motion.div>
                )}
                {/* Success State */}
                {details?.success && details.data?.correcao_dissertativa && (
                  <motion.div variants={criteriaVariants}>
                    <div className="correction-summary mb-4">
                      <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                        <div>
                          <h6 className="mb-1">Nota Total</h6>
                          <p className="text-muted mb-0">
                            Pontuação final da questão
                          </p>
                        </div>
                        <div
                          className={`h4 mb-0 ${getNotaColor(
                            details.data.correcao_dissertativa.nota_total
                          )}`}
                        >
                          {details.data.correcao_dissertativa.nota_total.toFixed(
                            1
                          )}
                          /10
                        </div>
                      </div>
                    </div>

                    {/* General Comment */}
                    {details.data.correcao_dissertativa.comentario_geral && (
                      <motion.div
                        variants={criteriaVariants}
                        className="general-comment mb-4"
                      >
                        <h6 className="text-muted">Comentário Geral:</h6>
                        <div className="comment-box p-3 bg-light rounded">
                          <p className="mb-0">
                            {
                              details.data.correcao_dissertativa
                                .comentario_geral
                            }
                          </p>
                        </div>
                      </motion.div>
                    )}

                    {/* Criteria Details */}
                    <div className="criteria-container">
                      <h6 className="text-muted mb-3">
                        Critérios de Avaliação:
                      </h6>
                      <div className="criteria-list">
                        {renderCriteria(details.data.correcao_dissertativa)}
                      </div>
                    </div>
                  </motion.div>
                )}
                {/* No Data State */}
                {details?.success && !details.data?.correcao_dissertativa && (
                  <motion.div variants={criteriaVariants}>
                    <Alert variant="info" className="text-center">
                      <Icon
                        icon={FaEye}
                        className="me-2"
                        aria-hidden={true}
                      />
                      Esta questão ainda não possui critérios detalhados de
                      correção disponíveis.
                    </Alert>
                  </motion.div>
                )}
              </motion.div>
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0">
              <Button variant="secondary" onClick={onHide}>
                Fechar
              </Button>
            </Modal.Footer>
          </motion.div>
        </Modal>
      )}
    </AnimatePresence>
  );
}
